/*
 * Форматирование для отображения. ВАЖНО: деньги/ставки приходят строками (Decimal).
 * Здесь мы парсим их в Number ТОЛЬКО для показа/сортировки — никакой денежной
 * логики во фронте нет (§1.2). Точность отображения, а не расчёта.
 */

const PCT = '%'

/** Безопасно превратить строку-Decimal в число (для показа). NaN → null. */
export function toNum(value: string | null | undefined): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Ставка фандинга (доля) → проценты со знаком. 0.0001 → "+0.0100%".
 *  Округление к нулю показываем как чистый ноль (без «-0.0000»). */
export function fundingPct(rate: string | null | undefined, digits = 4): string {
  const n = toNum(rate)
  if (n === null) return '—'
  const pct = n * 100
  const fixed = pct.toFixed(digits)
  const asNum = Number(fixed)
  if (asNum === 0) return `${(0).toFixed(digits)}${PCT}` // глушим знак у -0.0000
  return `${asNum > 0 ? '+' : ''}${fixed}${PCT}`
}

/** Размах/спред (доля) → проценты без знака «+» для нуля. */
export function spreadPct(rate: string | null | undefined, digits = 4): string {
  const n = toNum(rate)
  if (n === null) return '—'
  return `${(n * 100).toFixed(digits)}${PCT}`
}

/** Доля (число) → проценты. Для уже посчитанного спреда. */
export function pctNum(fraction: number, digits = 4): string {
  return `${(fraction * 100).toFixed(digits)}%`
}

/** Суточный объём (USDT) → компактная запись: 1.2M / 340K / $5 / «—». */
export function formatVolumeUsd(value: number | null): string {
  if (value === null) return '—'
  const abs = Math.abs(value)
  if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}M`
  if (abs >= 1e3) return `$${(value / 1e3).toFixed(1)}K`
  return `$${Math.round(value)}`
}

export type Sign = 'pos' | 'neg' | 'zero'

export function signOf(rate: string | null | undefined): Sign {
  const n = toNum(rate)
  if (n === null || n === 0) return 'zero'
  return n > 0 ? 'pos' : 'neg'
}

/** Цена для показа: разумные разряды по величине; тысячи — с разделителем. */
export function formatPrice(value: string | null | undefined): string {
  const n = toNum(value)
  if (n === null) return '—'
  const abs = Math.abs(n)
  let digits: number
  if (abs >= 1000) digits = 2
  else if (abs >= 1) digits = 4
  else digits = 6
  return n.toLocaleString('en-US', {
    minimumFractionDigits: digits >= 4 ? 2 : digits,
    maximumFractionDigits: digits,
  })
}

/** Интервал фандинга (сек) → «8ч» / «4ч» / «90м». */
export function formatInterval(sec: number): string {
  if (!sec || sec <= 0) return '—'
  if (sec % 3600 === 0) return `${sec / 3600}ч`
  if (sec % 60 === 0) return `${sec / 60}м`
  return `${sec}с`
}

/** Сколько осталось до момента (мс) — единый формат «Xч YYм ZZс»; без часов — «YYм ZZс».
 *  Нет таймстампа → «—»; прошедшее → «00м 00с». */
export function formatCountdown(targetMs: number, nowMs: number): string {
  if (!targetMs || targetMs <= 0) return '—'
  const totalSec = Math.max(0, Math.floor((targetMs - nowMs) / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}ч ${mm}м ${ss}с` : `${mm}м ${ss}с`
}

/** Близко ли фандинг (для подсветки «скоро»): ≤ порога минут. */
export function isFundingSoon(targetMs: number, nowMs: number, withinMin = 10): boolean {
  if (!targetMs || targetMs <= 0) return false
  const ms = targetMs - nowMs
  return ms > 0 && ms <= withinMin * 60000
}

/** «Обновлено N назад» — компактно. */
export function formatAgo(tsMs: number, nowMs: number): string {
  if (!tsMs) return '—'
  const sec = Math.max(0, Math.floor((nowMs - tsMs) / 1000))
  if (sec < 5) return 'только что'
  if (sec < 60) return `${sec} с назад`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} мин назад`
  const h = Math.floor(min / 60)
  return `${h} ч назад`
}

/**
 * Локальная дата ДД.ММ.ГГ — вторая строка под временем в ячейках-штампах.
 *
 * Год пишем всегда, хотя он почти никогда не нужен: ширина `ДД.ММ.ГГ` совпадает с
 * `ЧЧ:ММ:СС`, то есть столбец от даты не расширяется ни на пиксель, а история хранится до
 * `history_max_age_days` (деф. 365) — на границе года `ДД.ММ` стал бы неоднозначным.
 * Условный показ года сэкономил бы три символа ценой ветки и зависимости от «сейчас».
 */
export function formatDay(tsMs: number): string {
  if (!tsMs) return '—'
  return new Date(tsMs).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

/** Локальное время HH:MM:SS. */
export function formatClock(tsMs: number): string {
  if (!tsMs) return '—'
  return new Date(tsMs).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** Капитализировать имя биржи для показа (bybit → Bybit). */
export function exchangeLabel(name: string): string {
  if (!name) return name
  const known: Record<string, string> = { bybit: 'Bybit', gate: 'Gate', mexc: 'MEXC' }
  return known[name.toLowerCase()] ?? name.charAt(0).toUpperCase() + name.slice(1)
}

/** Детерминированный цвет-«дот» биржи (стабилен между рендерами). */
const EX_COLORS = ['#3b82f6', '#f7b955', '#2bd576', '#a78bfa', '#fb7185', '#38bdf8']
export function exchangeColor(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return EX_COLORS[h % EX_COLORS.length] as string
}

/** Деньги (строка-Decimal или число) → «$1,234.56». Знак опционально (для PnL). NaN → «—». */
export function formatUsd(
  value: string | number | null | undefined,
  { signed = false, digits = 2 }: { signed?: boolean; digits?: number } = {},
): string {
  const n = typeof value === 'number' ? value : toNum(value ?? null)
  if (n === null) return '—'
  const sign = signed && n > 0 ? '+' : ''
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
  return `${n < 0 ? '−' : sign}$${abs}`
}

/** Знак значения-строки для раскраски PnL. */
export function pnlSign(value: string | number | null | undefined): Sign {
  const n = typeof value === 'number' ? value : toNum(value ?? null)
  if (n === null || n === 0) return 'zero'
  return n > 0 ? 'pos' : 'neg'
}

/** Процент (число, напр. 0.76 = 0.76%) → «+0.76%». Уже в процентах, не доля. */
export function pctSigned(value: string | number | null | undefined, digits = 2): string {
  const n = typeof value === 'number' ? value : toNum(value ?? null)
  if (n === null) return '—'
  const fixed = n.toFixed(digits)
  if (Number(fixed) === 0) return `${(0).toFixed(digits)}%`
  return `${n > 0 ? '+' : '−'}${Math.abs(Number(fixed)).toFixed(digits)}%`
}

/** Длительность (мс) → «2д 3ч» / «5ч 12м» / «8м 03с» / «42с». */
export function formatDuration(ms: number): string {
  if (ms <= 0) return '0с'
  const totalSec = Math.floor(ms / 1000)
  const d = Math.floor(totalSec / 86400)
  const h = Math.floor((totalSec % 86400) / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (d > 0) return `${d}д ${h}ч`
  if (h > 0) return `${h}ч ${String(m).padStart(2, '0')}м`
  if (m > 0) return `${m}м ${String(s).padStart(2, '0')}с`
  return `${s}с`
}

/** Аптайм с момента старта (0 = бот не запущен → «—»). */
export function formatUptime(startMs: number, nowMs: number): string {
  if (!startMs) return '—'
  return formatDuration(Math.max(0, nowMs - startMs))
}

/** Внешняя ссылка на страницу фьючерса монеты на бирже (deep-link «Биржи»→trade). */
export function tradeUrl(exchange: string, coin: string): string | null {
  const c = coin.toUpperCase()
  switch (exchange.toLowerCase()) {
    case 'bybit':
      return `https://www.bybit.com/trade/usdt/${c}USDT`
    case 'gate':
      return `https://www.gate.io/futures/USDT/${c}_USDT`
    case 'mexc':
      return `https://futures.mexc.com/exchange/${c}_USDT`
    default:
      return null
  }
}
